import { IHypotheekResponse } from './IHypotheekResponse';
import { IHypotheekdeel } from './IHypotheekdeel';
import { IOpenstaandeBetaling } from './IOpenstaandeBetaling';
import moment from 'moment';
import * as _ from 'lodash';
import { IOnderpand } from './IOnderpand';
import { Risicoklasse } from './Risicoklasse';
import { VoorkeurenNotificaties} from './VoorkeurenNotificaties';
import { IBouwdepot } from './IBouwdepot';
import { IBetaling } from './IBetaling';
import store from '../store';

export class Hypotheek {
  public id!: string;
  public relatienummer!: string;
  public notificaties: VoorkeurenNotificaties;
  public notificatieEmail!: string;
  public bouwdepot!: IBouwdepot;
  public hypotheekdelen!: IHypotheekdeel[];
  public onderpanden!: IOnderpand[];
  public openstaandeBetalingen!: IOpenstaandeBetaling[];
  public risicoklasse: Risicoklasse;
  public hypotheekgevers!: string[];
  public loanToValue!: number;

  constructor(value: IHypotheekResponse, private allBetalingen: IBetaling[]) {
    Object.assign(this, value);
    this.risicoklasse = Risicoklasse[value.risicoklasse as keyof typeof Risicoklasse];
    this.notificaties = VoorkeurenNotificaties[value.notificaties as keyof typeof VoorkeurenNotificaties];
  }

  get title(): string {
    return this.hypotheekdelen.map(d => d.hypotheeknummer).join(', ');
  }

  get marktwaarde(): number {
    return this.onderpanden ? this.onderpanden.reduce((total, onderpand) => (total += onderpand.marktwaarde), 0) : 0;
  }

  get origineleSchuld(): number {
    return this.hypotheekdelen.reduce((val, d) => val + d.origineleSchuld, 0);
  }

  get huidigeSchuld(): number {
    return this.hypotheekdelen.reduce((val, d) => val + d.huidigeSchuld, 0);
  }

  get brutoMaandlast(): number {
    return this.hypotheekdelen.reduce((val, d) => val + d.brutoMaandlast.totaal, 0);
  }

  get eindeRentevastePeriode(): Date {
    return moment.min(this.hypotheekdelen.map(d => moment(d.eindeRentevastePeriode))).toDate();
  }

  get minRente(): number {
    return Math.min.apply(
      null,
      this.hypotheekdelen.map(d => d.rente)
    );
  }

  get maxRente(): number {
    return Math.max.apply(
      null,
      this.hypotheekdelen.map(d => d.rente)
    );
  }

  get afgelosteSchuld(): number {
    return this.origineleSchuld - this.huidigeSchuld;
  }

  get betalingen(): IBetaling[] {
    return _.filter(
      this.allBetalingen,
      b => !!_.find(this.hypotheekdelen, hd => hd.hypotheeknummer === b.hypotheekdeelnummer)
    );
  }

  get openBetaling(): IBetaling | undefined {
    return _.find(this.betalingen, b => b.paymentProviderStatus === 'open');
  }

  get paidBetalingen(): IBetaling[] {
    return _.filter(this.betalingen, b => b.paymentProviderStatus === 'paid');
  }

  public paidBetaling(hypotheekdeel: IHypotheekdeel) {
    return _.find(this.betalingen, b => b.hypotheekdeelnummer === hypotheekdeel.hypotheeknummer);
  }

  get hasPaidBetaling(): boolean {
    return this.paidBetalingen.length > 0;
  }

  public canHypotheekdeelAflossen(hypotheekdeel: IHypotheekdeel, ignorePaidBetaling = false): boolean {
    return (
      !store.state.config?.iDealPaymentsDisabled &&
      hypotheekdeel.isExtraAflossingToegestaan &&
      hypotheekdeel.hypotheekvorm !== 'Spaarhypotheek' &&
      (ignorePaidBetaling || !_.find(this.paidBetalingen, b => b.hypotheekdeelnummer === hypotheekdeel.hypotheeknummer))
    );
  }

  get aflosbareHypotheekdelen(): IHypotheekdeel[] {
    return _.filter(this.hypotheekdelen, d => this.canHypotheekdeelAflossen(d));
  }

  get canAflossen(): boolean {
    return this.aflosbareHypotheekdelen.length > 0;
  }

  get canWoningwaardeWijzigen(): boolean {
    return this.risicoklasse === Risicoklasse.KleinerDanOfGelijkAan65ProcentMarktwaarde;
  }
}
